import axios from 'axios';
import { API_BASE_URL, AUTH_ENDPOINTS } from '../config/api';

// Create a dedicated axios instance for auth
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store',
    'Pragma': 'no-cache'
  },
  withCredentials: true
});

// Add request interceptor to add timestamp to prevent caching
authApi.interceptors.request.use(
  config => {
    // Add timestamp to prevent caching
    config.headers['X-Timestamp'] = Date.now().toString();
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor for debugging
authApi.interceptors.response.use(
  response => {
    console.log(`Auth API response success: ${response.config.url}`);
    return response;
  },
  error => {
    if (error.response) {
      console.error(`Auth API error (${error.response.status}): ${error.config.url}`, error.response.data);
    } else if (error.request) {
      console.error(`Auth API no response: ${error.config?.url || 'unknown endpoint'}`, error.request);
    } else {
      console.error(`Auth request error: ${error.message}`);
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_role: string;
  email: string;
  name: string;
  publisher_id?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Attempts to log in with the provided credentials
 * Implements retry logic for sporadic auth issues
 */
export const login = async (credentials: LoginCredentials, maxRetries = 1): Promise<LoginResponse> => {
  let retryCount = 0;
  let lastError: any = null;
  
  while (retryCount <= maxRetries) {
    try {
      console.log(`Auth service: login attempt ${retryCount + 1}/${maxRetries + 1}`);
      
      // Use endpoint from config, but strip base URL since axios instance has it
      const loginEndpoint = AUTH_ENDPOINTS.LOGIN.replace(API_BASE_URL, '');
      const response = await authApi.post<LoginResponse>(loginEndpoint, credentials);
      
      return response.data;
    } catch (err) {
      lastError = err;
      console.error(`Login error (attempt ${retryCount + 1}):`, err);
      
      if (retryCount < maxRetries) {
        retryCount++;
        // Wait a short time before retry - increase delay with each retry
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      } else {
        break;
      }
    }
  }
  
  // All retries failed
  throw lastError || new Error('Authentication failed after multiple attempts');
};

/**
 * Verifies if the current token is valid
 */
export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    // Use endpoint from config, but strip base URL since axios instance has it
    const checkTokenEndpoint = AUTH_ENDPOINTS.CHECK_TOKEN.replace(API_BASE_URL, '');
    const response = await authApi.get(checkTokenEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.status === 200;
  } catch (error) {
    console.error('Token verification failed:', error);
    return false;
  }
};

/**
 * Logs the user out
 */
export const logout = async (): Promise<void> => {
  try {
    // If your backend has a logout endpoint, call it here
    // await authApi.post('/auth/logout');
    
    // Clear any auth-related cookies if needed
    document.cookie.split(';').forEach(cookie => {
      document.cookie = cookie
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
    });
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('email');
    localStorage.removeItem('name');
    localStorage.removeItem('publisherId');
    
    console.log('Logged out successfully');
  } catch (error) {
    console.error('Logout error:', error);
  }
};

export default {
  login,
  verifyToken,
  logout
}; 